import { getReportTableSx } from './report-table-styles';
import { createTheme, darken, getContrastRatio, lighten } from '@mui/material/styles';

// Data colors stay readable even when the client's secondary brand color is white.
export const reportColors = {
    revenue: '#2563EB',
    conversions: '#0F766E',
    categories: ['#2563EB', '#0F766E', '#7C3AED', '#B45309', '#BE185D', '#475569', '#0369A1', '#4D7C0F']
};

// Fixed data semantics; saved brand colors continue to theme controls and navigation.
export const analyticsColors = {
    acquisition: reportColors.revenue,
    engagement: reportColors.conversions,
    outcome: reportColors.categories[3]
};

export const getReportColors = (theme) => {
    const paper = theme.palette.background.paper;
    const readable = color => getContrastRatio(color, paper) >= 3 ? color
        : theme.palette.mode === 'dark' ? lighten(color, 0.6) : darken(color, 0.6);
    const primary = readable(theme.palette.primary.main);
    const secondary = readable(theme.palette.secondary.main);
    const accent = getContrastRatio(primary, secondary) >= 1.3 ? secondary
        : getContrastRatio(primary, reportColors.conversions) >= 1.3 ? reportColors.conversions : reportColors.categories[2];
    return { revenue: primary, conversions: accent, categories: [primary, accent, ...reportColors.categories.slice(2)] };
};

export const styleReportChart = (theme, options) => {
    const palette = getReportColors(theme);
    const colors = options.colors?.map(color => {
        const index = reportColors.categories.indexOf(color);
        return index < 0 ? color : options.colors.length === 1 ? palette.revenue : palette.categories[index];
    });
    return ({
    ...options,
    ...(colors ? { colors } : {}),
    chart: {
        ...options.chart,
        fontFamily: theme.typography.fontFamily,
        foreColor: theme.palette.text.secondary,
        background: 'transparent',
        toolbar: { show: false },
        animations: { enabled: false }
    },
    fill: { type: 'solid', opacity: 1 },
    grid: { borderColor: theme.palette.divider, strokeDashArray: 4, padding: { left: 8, right: 12 } },
    stroke: {
        ...options.stroke,
        width: options.chart.type === 'line' ? (options.stroke?.width ?? 3) : ['donut', 'treemap', 'bubble'].includes(options.chart.type) ? 2 : 0,
        colors: options.chart.type === 'line' ? colors : [theme.palette.background.paper]
    },
    legend: { ...options.legend, fontSize: '12px', labels: { colors: theme.palette.text.secondary }, itemMargin: { horizontal: 10, vertical: 5 } },
    xaxis: {
        ...options.xaxis,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { ...options.xaxis?.labels, style: { fontSize: '12px' }, trim: true, hideOverlappingLabels: true }
    },
    yaxis: Array.isArray(options.yaxis)
        ? options.yaxis.map((axis) => ({ ...axis, min: 0, labels: { ...axis.labels, style: { fontSize: '12px' } } }))
        : { ...options.yaxis, min: 0, labels: { ...options.yaxis?.labels, style: { fontSize: '12px' } } },
    states: { hover: { filter: { type: 'darken', value: 0.9 } }, active: { filter: { type: 'none' } } }
});
};

export const createReportSurfaceTheme = (brandTheme) => createTheme({
    ...brandTheme,
    palette: {
        ...brandTheme.palette,
        background: { default: '#F8FAFC', paper: '#FFFFFF' },
        text: { ...brandTheme.palette.text, primary: '#0F172A', secondary: '#475569' },
        divider: '#E2E8F0'
    }
});

export const getReportSurfaceSx = (theme) => ({
    borderRadius: 3,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)',
    background: theme.palette.background.paper,
    overflow: 'hidden',
    minWidth: 0
});

// GA4's neutral report canvas and tab treatment, shared across channel reports.
export const getReportPageSx = (theme) => ({
    p: { xs: 1.5, md: 3 }, bgcolor: theme.palette.background.default, color: theme.palette.text.primary,
    minWidth: 0, display: 'grid', gap: 2,
    '& .MuiCard-root': getReportSurfaceSx(theme),
    '& .MuiCardHeader-root': { p: 2.5, borderBottom: `1px solid ${theme.palette.divider}`, background: theme.palette.background.paper, backdropFilter: 'none' },
    '& .MuiCardHeader-title': { fontSize: '1rem', fontWeight: 700 },
    '& .MuiCardHeader-subheader': { fontSize: '0.875rem', color: theme.palette.text.secondary, mt: 0.5, letterSpacing: 'normal', textTransform: 'none' },
    '& .MuiCardContent-root': { p: { xs: 1.5, md: 2.5 } },
    '& .MuiTableContainer-root': { maxWidth: '100%', overflowX: 'auto' },
    '& .MuiTableCell-root': { px: 1.5, py: 1.25, borderColor: theme.palette.divider, fontVariantNumeric: 'tabular-nums' },
    '& .MuiTableCell-head': { background: `${theme.palette.background.default} !important`, color: `${theme.palette.text.primary} !important` },
    ...getReportTableSx(theme),
    '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)': { bgcolor: theme.palette.background.default },
    '@media print': { p: 0, bgcolor: 'white' }
});

export const getAnalyticsReportTabsSx = () => ({
    minHeight: 44,
    '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 700 },
    '& .MuiTab-root.Mui-selected': { bgcolor: 'transparent', color: 'primary.main' },
    '& .MuiTabs-indicator': { bgcolor: 'primary.main' }
});
